import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import {
  Users,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Heart,
  Shield
} from 'lucide-react';

interface AffiliateFormData {
  name: string;
  email: string;
  website: string;
  socialMedia: string;
  audience: string;
  reason: string;
}

const AffiliateSignup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AffiliateFormData>({
    name: '',
    email: '',
    website: '',
    socialMedia: '',
    audience: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const registerAffiliatePublic = httpsCallable(functions, 'registerAffiliatePublic');
      await registerAffiliatePublic({
        name: formData.name,
        email: formData.email,
        website: formData.website || undefined,
        socialMedia: formData.socialMedia || undefined,
        audience: formData.audience,
        reason: formData.reason
      });
      setSubmitted(true);
    } catch (err: unknown) {
      console.error('Affiliate signup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Application Received - Affiliate Program | SWTPA</title>
        </Helmet>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
          <div className="container-base py-16 sm:py-24">
            <div className="max-w-xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success mb-8 animate-bounce">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="heading-1 dark:text-white mb-4">
                Application Received!
              </h1>
              <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-8">
                Thank you for your interest in partnering with Social Work Test Prep Academy.
                We'll review your application and get back to you within 2-3 business days.
              </p>
              <p className="text-base text-neutral-500 dark:text-neutral-400 mb-4">
                Check your email at <strong className="dark:text-white">{formData.email}</strong> for confirmation.
              </p>
              <p className="text-base text-neutral-500 dark:text-neutral-400 mb-8">
                Once approved, create your account at{' '}
                <button
                  onClick={() => navigate('/affiliate/signin')}
                  className="text-primary-600 hover:underline font-medium"
                >
                  /affiliate/signin
                </button>
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Return Home
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Become an Affiliate - Social Work Test Prep Academy</title>
        <meta name="description" content="Join our affiliate program and earn 20% commission helping social workers pass their licensing exams. Partner with SWTPA today." />
        <meta property="og:title" content="Become an Affiliate Partner - SWTPA" />
        <meta property="og:description" content="Earn 20% commission for every referral. Help social workers succeed while growing your income." />
      </Helmet>

      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-primary-700">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full bg-white blur-3xl" />
          </div>

          <div className="container-base py-16 sm:py-24 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 caption text-primary-200 bg-white/10 px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
                <Sparkles className="w-4 h-4" />
                Partner Program
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                Help Social Workers Succeed.<br />
                <span className="text-primary-200">Earn While You Do.</span>
              </h1>
              <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
                Join our affiliate program and earn <strong className="text-white">20% commission</strong> for
                every student you refer. Together, we're building a community of confident, licensed social workers.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="container-base -mt-8 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: DollarSign, label: '20% Commission', desc: 'On every referral' },
              { icon: Users, label: '90-Day Cookies', desc: 'Extended attribution' },
              { icon: TrendingUp, label: 'Monthly Payouts', desc: 'Via Stripe Connect' }
            ].map((stat, i) => (
              <div
                key={i}
                className="card dark:bg-neutral-800 shadow-ledge hover:shadow-ledge-lg transition-shadow duration-300 flex items-center gap-4"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900 dark:text-white">{stat.label}</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{stat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="container-base py-16">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Left: Why Partner */}
            <div className="space-y-8">
              <div>
                <h2 className="heading-2 text-neutral-900 dark:text-white mb-4">
                  Why Partner With Us?
                </h2>
                <p className="text-base text-neutral-600 dark:text-neutral-300">
                  We give social workers an adaptive, focused way to prepare for the ASWB exam.
                  When you refer your audience to SWTPA, you're giving them access to
                  an adaptive learning platform built for exam preparation.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: Heart,
                    title: 'Mission-Aligned',
                    desc: 'Support the next generation of social workers in their licensing journey.'
                  },
                  {
                    icon: DollarSign,
                    title: 'Generous Commissions',
                    desc: 'Earn 20% on every subscription. No caps, no limits.'
                  },
                  {
                    icon: Shield,
                    title: 'Trusted Platform',
                    desc: 'High conversion rates because students love our adaptive technology.'
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 transition-colors">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 dark:bg-neutral-700 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Right: Application Form */}
            <div className="lg:sticky lg:top-8 lg:self-start">
              <div className="card dark:bg-neutral-800 shadow-ledge-lg dark:border-neutral-700 p-8">
                <h2 className="text-lg font-display font-semibold text-neutral-900 dark:text-white mb-2">
                  Apply to Join
                </h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                  Tell us a bit about yourself and how you'll promote SWTPA.
                </p>

                {error && (
                  <div className="mb-6 p-4 rounded-lg bg-error-light border border-error text-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Jane Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="jane@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Website or Blog
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="https://yoursite.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="socialMedia" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Social Media Handles
                    </label>
                    <input
                      type="text"
                      id="socialMedia"
                      name="socialMedia"
                      value={formData.socialMedia}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="@yourusername on Instagram, TikTok, etc."
                    />
                  </div>

                  <div>
                    <label htmlFor="audience" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Describe Your Audience *
                    </label>
                    <select
                      id="audience"
                      name="audience"
                      required
                      value={formData.audience}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select your primary audience</option>
                      <option value="msw_students">MSW Students</option>
                      <option value="bsw_students">BSW Students</option>
                      <option value="practicing_social_workers">Practicing Social Workers</option>
                      <option value="educators">Social Work Educators/Professors</option>
                      <option value="career_coaches">Career Coaches/Mentors</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      How Will You Promote SWTPA? *
                    </label>
                    <textarea
                      id="reason"
                      name="reason"
                      required
                      rows={3}
                      value={formData.reason}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your promotion plans..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full py-4 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-neutral-500 dark:text-neutral-400">
                    By applying, you agree to our affiliate terms and conditions.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AffiliateSignup;
